/**
 * Leaflet map context — spec §F5.1 / §F2.
 *
 * `MapView` owns the single `L.Map` instance and publishes it here; every child widget
 * (hierarchy layers, status bar, and from M2 the legend/dock/timebar) reads it with
 * `useMap()` instead of receiving it through props. Children are only rendered once the
 * map exists, so `useMap()` inside them is never null in practice — `useMapOrNull()` is
 * for the rare component that renders outside the map tree.
 */
import { createContext, useContext, useEffect, useState } from 'react';
import type L from 'leaflet';

export const MapContext = createContext<L.Map | null>(null);

export function useMapOrNull(): L.Map | null {
  return useContext(MapContext);
}

export function useMap(): L.Map {
  const map = useContext(MapContext);
  if (!map) throw new Error('useMap() called outside <MapView>');
  return map;
}

export interface MapViewState {
  zoom: number;
  center: L.LatLng;
}

/**
 * The map's zoom/centre as React state. Leaflet is the source of truth (it mutates its
 * own view on pan/zoom); this hook mirrors it so components re-render on `moveend`.
 */
export function useMapViewState(map: L.Map): MapViewState {
  const [view, setView] = useState<MapViewState>(() => ({
    zoom: map.getZoom(),
    center: map.getCenter(),
  }));
  useEffect(() => {
    const sync = () => setView({ zoom: map.getZoom(), center: map.getCenter() });
    sync();
    map.on('moveend zoomend', sync);
    return () => {
      map.off('moveend zoomend', sync);
    };
  }, [map]);
  return view;
}
